/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-get, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
// Extend From
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
// Ember Imports
import EmberObject, { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import moment from 'moment';
// External imports
// Internal Imports
import {
  SuspensionTabs,
  ApprovalReason,
} from '@linc-technologies/linced-common/enums/suspensions';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';

export default class SuspensionActionManager extends Component {
  // Services
  @service
  behaviourDates;

  @service
  dates;

  @service
  options;

  @service
  session;

  @service
  store;

  @service
  suspensions;

  @service
  toaster;

  ApprovalReason = ApprovalReason;
  SuspensionTabs = SuspensionTabs;

  // Modal options

  // Params
  @tracked missingConsultationDate = false;

  @tracked needsApproval = false;
  @tracked outstandingApprovals = null;
  @tracked declinedApprovals = null;
  @tracked principal = null;
  suspensionPage = null;
  suspensionLettersPage = null;

  @optionAlias('hero:behaviours:suspensionDurationUpTo')
  suspensionDurationUpTo;

  @computed('suspension.{consultationDate,nextSchoolDay,startDate}')
  get caregiverMeetingDateWarning() {
    let returnDate = this.get('suspension.nextSchoolDay');
    let startDate = this.get('suspension.startDate');
    let meetingDate = this.get('suspension.consultationDate');

    if (isEmpty(meetingDate) || isEmpty(startDate) || isEmpty(returnDate)) {
      return '';
    }

    // must be a school day
    if (!this.behaviourDates.validateIsSchoolDayOrEmpty(meetingDate)) {
      return 'Meeting dates must fall on school days';
    }

    // reassign to moments for comparison
    meetingDate = moment(meetingDate, 'YYYY-MM-DD');
    startDate = moment(startDate, 'YYYY-MM-DD');
    returnDate = moment(returnDate, 'YYYY-MM-DD');

    // review date must be between start date and end date
    return meetingDate.isSameOrAfter(startDate, 'day') &&
      meetingDate.isSameOrBefore(returnDate, 'day')
      ? ''
      : 'Meeting date must be within the suspension timeframe, or on the first day back';
  }

  @computed(
    'outstandingApprovals.@each.{approved,date}',
    'declinedApprovals.@each.{approved,date}',
  )
  get isValid() {
    const outstandingApprovals = this.outstandingApprovals;
    const declinedApprovals = this.declinedApprovals;

    if (outstandingApprovals.length > 0 || declinedApprovals.length > 0) {
      const totalApprovals = outstandingApprovals.concat(declinedApprovals);
      const approvalInvalid = totalApprovals.some((approval) => {
        return approval.approved && !approval.date;
      });

      if (approvalInvalid) {
        return false;
      }
    }
    return true;
  }

  get principalDetails() {
    // No principal found, just return an empty object for now
    if (isEmpty(this.principal)) {
      return EmberObject.create({});
    }
    return {
      name: this.principal.fullName,
      signature: this.principal.firstName[0] + this.principal.lastName[0],
    };
  }

  // Hacky id check for porters creek. Dynamic editable letter templates will replace this when they arrive. Temporary
  @computed('session.school.id')
  get isPortersCreek() {
    return (
      this.get('session.school.id') === '3d410502-b223-458e-ac0a-c6fa26ca5f8c'
    );
  }

  calculateRequiredActions() {
    switch (this.get('suspension.tab')) {
      case SuspensionTabs.PENDING:
        if (this.get('suspension.category') > 0) {
          this.needsApproval = true;
        }
        break;
      case SuspensionTabs.FORMAL_CAUTIONS:
        if (isEmpty(this.get('suspension.consultationDate'))) {
          this.missingConsultationDate = true;
        }
        break;
      case SuspensionTabs.SUSPENSIONS:
        if (this.get('suspension.needsApproval')) {
          this.needsApproval = true;
        }
        if (isEmpty(this.get('suspension.consultationDate'))) {
          this.missingConsultationDate = true;
        }
        break;
      case SuspensionTabs.EXTENDED_SUSPENSIONS:
        if (this.get('suspension.needsApproval')) {
          this.needsApproval = true;
        }
        if (isEmpty(this.get('suspension.consultationDate'))) {
          this.missingConsultationDate = true;
        }
        break;
    }
  }

  calculateOutstandingApprovals() {
    let declined = [];
    let outstanding = [];
    this.get('suspension.approvals').forEach((request) => {
      if (request.declined) {
        declined.push(request);
      }
      if (!request.approved && !request.declined) {
        outstanding.push(request);
      }
    });

    this.outstandingApprovals = outstanding;
    this.declinedApprovals = declined;
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    this.missingConsultationDate = false;
    this.needsApproval = false;
    this.outstandingApprovals = null;
    if (this.dialogOpen) {
      this.calculateRequiredActions();
      this.calculateOutstandingApprovals();

      this.suspensions.initialise(
        '',
        this.principalDetails,
        this.suspension,
        this.suspensionPage,
        this.suspensionLettersPage,
      );
    }
  }

  processSuspensionApproval() {
    let suspension = this.suspension;
    return Promise.all(
      this.suspensions.processSuspensionDocuments(suspension, suspension),
    );
  }

  @action
  save() {
    this.calculateOutstandingApprovals();

    return Promise.resolve()
      .then(() => {
        if (
          this.outstandingApprovals.length === 0 &&
          this.declinedApprovals.length === 0
        ) {
          this.set('suspension.needsApproval', false);
          return this.processSuspensionApproval();
        }
      })
      .then(() => {
        return this.suspension
          .save()
          .then(() => {
            this.toaster.success('Updated Successfully');
            this.close();
          })
          .catch(() => {
            this.toaster.error('Error updating suspension');
          });
      });
  }

  @action
  setApprovalDate(approval, date) {
    approval.set('date', moment(date, 'YYYY-MM-DD').unix());
  }

  @action
  closeDialog() {
    this.suspension.rollbackAttributes();
    this.close();
  }

  @action
  noop() {
    //do nothing
  }

  @action
  setApprovalStatus(approval, status) {
    switch (status) {
      case 'approved':
        approval.setProperties({
          approved: true,
          declined: false,
        });
        break;
      case 'declined':
        approval.setProperties({
          approved: false,
          declined: true,
        });
        break;
    }
  }
}
